import type { AccessSourceType, PermissionUpdateEntry } from '@thinkalpha/common/contracts/permissions.js';
import type {
    AddPoolItemsRequest,
    AddPoolItemsResponse,
    GetPoolChangeLogResponse,
    GetPoolResponse,
    ListPoolItemsResponse,
    ListPoolsResponse,
    RemovePoolItemResponse,
    UpdatePoolItemRequest,
    UpdatePoolItemResponse,
    UpdatePoolRequest,
    UpdatePoolResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pools';
import { lapi as axios } from 'src/api';

/**
 * HTTP wrappers around the locates-ws `Inventory (Modern)` endpoints.
 * Read code lives alongside writes here, but the React-Query factory
 * files in {@link ../labPools} and {@link ../labPoolsMutations} split
 * read vs. write so callers can stay on the side they need.
 *
 * Backend swagger:
 *   GET    /v1/inventory/pools                       — ListPools
 *   GET    /v1/inventory/pools?firmId=<uuid>         — ListPools (firm-scoped)
 *   GET    /v1/inventory/pools/{poolId}                — GetPool (Inspect a pool)
 *   GET    /v1/inventory/pools/{poolId}/items        — ListPoolItems
 *   GET    /v1/inventory/pools/{poolId}/items?symbol=… — ListPoolItems (symbol-filtered)
 *   POST   /v1/inventory/pools/{poolId}/items        — AddPoolItems (async, batched 50)
 *   PATCH  /v1/inventory/pools/{poolId}/items/{id}   — UpdatePoolItem
 *                                                     NB: price change replaces
 *                                                     the row — response.item.id
 *                                                     may differ from the URL id
 *   DELETE /v1/inventory/pools/{poolId}/items/{id}   — RemovePoolItem (501 today;
 *                                                     unblocks when backend ships
 *                                                     the SQL procedure)
 *
 * The `AddPoolItems` upload is acknowledged synchronously with
 * `{ uploadId, accepted }`; the items themselves arrive over the
 * `v1.inventory.pools.item` WebSocket stream once the client has
 * joined the pool room. The WS integration is not wired yet; the
 * model relies on `invalidatePoolItemsQuery` to refetch after writes
 * settle.
 */

export const getAllPools = (): Promise<ListPoolsResponse> => {
    return axios.get<ListPoolsResponse>('/v1/inventory/pools').then((x) => x.data);
};

export const getFirmPools = (firmId: string): Promise<ListPoolsResponse> => {
    return axios.get<ListPoolsResponse>('/v1/inventory/pools', { params: { firmId } }).then((x) => x.data);
};

/**
 * Inspect a single pool by id. Returns the same `InventoryPool`
 * shape that appears in `ListPoolsResponse.pools`, useful for
 * deep-link entry points that need the pool metadata before (or
 * without) loading the full pool list.
 */
export const getPool = (poolId: string): Promise<GetPoolResponse> => {
    return axios.get<GetPoolResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}`).then((x) => x.data);
};

export interface GetPoolItemsOptions {
    /**
     * Exact-match ticker filter. The backend compares `sym = @sym` with
     * no case normalization, so this is case-sensitive against the
     * upper-case stored symbols — callers should upper-case the term.
     * When set, the backend returns every matching item and ignores
     * `skip` / `limit`.
     */
    symbol?: string;
    /** Zero-based starting row. Ignored when `symbol` is set. */
    skip?: number;
    /** Page size. Ignored when `symbol` is set. */
    limit?: number;
}

/**
 * List a pool's items, optionally paged. Returns `{ items, count }`
 * where `count` is the total matching rows in the pool (so the client
 * can paginate without a second `HEAD`-style call). When `symbol` is
 * supplied, paging is bypassed by the backend.
 */
export const getPoolItems = (poolId: string, options?: GetPoolItemsOptions): Promise<ListPoolItemsResponse> => {
    const params: Record<string, string | number> = {};
    if (options?.symbol) params.symbol = options.symbol;
    if (options?.skip !== undefined) params.skip = options.skip;
    if (options?.limit !== undefined) params.limit = options.limit;
    return axios
        .get<ListPoolItemsResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/items`, {
            params: Object.keys(params).length ? params : undefined,
        })
        .then((x) => x.data);
};

export interface GetPoolChangeLogOptions {
    skip?: number;
    limit?: number;
}

/**
 * Page through a pool's audit log (sourced from the legacy SQL
 * Journal). Returns `{ entries, count }`. The backend's note flags
 * the contract as "untested, subject to change", so do not consume
 * the raw `action` integer without a fallback for unknown codes.
 */
export const getPoolChangeLog = (
    poolId: string,
    options?: GetPoolChangeLogOptions,
): Promise<GetPoolChangeLogResponse> => {
    const params: Record<string, number> = {};
    if (options?.skip !== undefined) params.skip = options.skip;
    if (options?.limit !== undefined) params.limit = options.limit;
    return axios
        .get<GetPoolChangeLogResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/log`, {
            params: Object.keys(params).length ? params : undefined,
        })
        .then((x) => x.data);
};

export const addPoolItems = ({
    poolId,
    request,
}: {
    poolId: string;
    request: AddPoolItemsRequest;
}): Promise<AddPoolItemsResponse> => {
    return axios
        .post<AddPoolItemsResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/items`, request)
        .then((x) => x.data);
};

/**
 * Update an item's `quantity` and/or `price`.
 *
 * IMPORTANT: a price change is implemented server-side as a row
 * replacement, not an in-place update. When the request body includes
 * `price`, `response.item.id` will differ from the `{itemId}` passed
 * in the URL. Callers that maintain a local id-keyed cache must reconcile
 * — either by patching the cache with the new `item.id` directly, or by
 * invalidating the items query so the refetch picks up the new id. The
 * model takes the invalidation path; if/when we add optimistic updates,
 * use `response.item` rather than re-keying by the URL id.
 */
export const updatePoolItem = ({
    poolId,
    itemId,
    request,
}: {
    poolId: string;
    itemId: string;
    request: UpdatePoolItemRequest;
}): Promise<UpdatePoolItemResponse> => {
    return axios
        .patch<UpdatePoolItemResponse>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/items/${encodeURIComponent(itemId)}`,
            request,
        )
        .then((x) => x.data);
};

/**
 * Remove an item by id. Currently returns `501 Not Implemented` —
 * backend will ship the SQL procedure later. The trash-can affordance
 * is already wired through `submitChanges` so it will begin working
 * automatically without a UI change.
 */
export const removePoolItem = ({
    poolId,
    itemId,
}: {
    poolId: string;
    itemId: string;
}): Promise<RemovePoolItemResponse> => {
    return axios
        .delete<RemovePoolItemResponse>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/items/${encodeURIComponent(itemId)}`,
        )
        .then((x) => x.data);
};

/**
 * Toggle a pool's `active` flag. Hits `PATCH /v1/inventory/pools/{poolId}`
 * with `{ active }`. Returns the updated pool — model code should reconcile
 * its cache against `response.pool` rather than the request value, in case
 * the backend reshapes anything else server-side.
 */
export const updatePool = ({
    poolId,
    request,
}: {
    poolId: string;
    request: UpdatePoolRequest;
}): Promise<UpdatePoolResponse> => {
    return axios
        .patch<UpdatePoolResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}`, request)
        .then((x) => x.data);
};

/**
 * Replace the set of permission entries that `entityType`/`entityId`
 * holds on `poolId`. Send `entries` to grant the listed accesses (each
 * entry has its own `kind: 'allow' | 'deny'`); send an empty array to
 * revoke. Backend requires `owner` permission on the pool. Returns
 * 204; the model should refetch the pool after the call settles to
 * pick up the new `permissions` payload.
 */
export const updatePoolPermissionsForEntity = ({
    poolId,
    entityType,
    entityId,
    entries,
}: {
    poolId: string;
    entityType: AccessSourceType;
    entityId: string;
    entries: PermissionUpdateEntry[];
}): Promise<void> => {
    return axios
        .put<void>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/permissions/${encodeURIComponent(
                entityType,
            )}/${encodeURIComponent(entityId)}`,
            entries,
        )
        .then(() => undefined);
};
